import React, { useEffect, useState } from 'react';
import { api } from '../api/client';
import { Button, inputStyle, tokens } from './ui/controls';

export function FormPicker({
  kind,
  spec,
  onSpec,
}: {
  kind: string;
  spec: Record<string, unknown>;
  onSpec: (spec: Record<string, unknown>) => void;
}) {
  const [names, setNames] = useState<string[]>([]);
  const [selected, setSelected] = useState('');
  const [saveName, setSaveName] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    setSelected('');
    setSaveName('');
    setMessage('');
    setError('');
    api.dataviewer.listFormTemplates(kind)
      .then((res) => setNames(res.data.names || []))
      .catch(() => setNames([]));
  }, [kind]);

  const save = async () => {
    const name = saveName.trim();
    if (!name || name.includes('/')) {
      setError('Enter a template name without slashes');
      setMessage('');
      return;
    }
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await api.dataviewer.saveFormTemplate(kind, name, spec);
      setNames((current) => (current.includes(name) ? current : [...current, name].sort((a, b) => a.localeCompare(b))));
      setSelected(name);
      setMessage(`Saved ${name}`);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Could not save template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'grid', gap: '0.45rem' }}>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.8rem', fontWeight: 600, color: tokens.textMuted }}>Template</span>
        <select
          aria-label="Template"
          value={selected}
          onChange={(e) => {
            setSelected(e.target.value);
            setSaveName(e.target.value);
            setMessage('');
            setError('');
          }}
          style={{ ...inputStyle, width: 'auto', minWidth: 180 }}
        >
          <option value="">Blank</option>
          {names.map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
        <Button
          disabled={!selected}
          onClick={async () => {
            const res = await api.dataviewer.getFormTemplate(kind, selected);
            onSpec(res.data as Record<string, unknown>);
            setSaveName(selected);
            setMessage('');
            setError('');
          }}
        >
          Load
        </Button>
        <Button variant="subtle" onClick={() => {
          onSpec({});
          setSaveName('');
          setMessage('');
          setError('');
        }}
        >
          Reset to blank
        </Button>
        <input
          aria-label="Template name"
          value={saveName}
          onChange={(event) => setSaveName(event.target.value)}
          placeholder="Template name"
          style={{ ...inputStyle, width: 200 }}
        />
        <Button onClick={save} disabled={saving || !saveName.trim()}>
          {saving ? 'Saving…' : 'Save template'}
        </Button>
        {names.length === 0 && (
          <span style={{ fontSize: '0.8rem', color: tokens.textFaint }}>No saved templates for this job type</span>
        )}
      </div>
      {message && <span style={{ fontSize: '0.8rem', color: tokens.textMuted }}>{message}</span>}
      {error && <span style={{ fontSize: '0.8rem', color: tokens.danger }}>{error}</span>}
    </div>
  );
}
